/**
 * Inventário das superfícies de conteúdo gerado por IA — a fonte única de
 * `surface` para content_reports.
 *
 * Contexto (18/08/2026): o Google rejeitou a 1.4.0 por falta de denúncia
 * in-app de conteúdo de IA, e avisou por escrito que "esse problema também
 * pode aparecer em outros lugares — confira TODAS as áreas do app". A
 * evidência anexada mostrava só duas telas (imagem gerada + Espelho); este
 * arquivo é a resposta à outra frase, e é ele que impede um segundo
 * reprovado por uma terceira superfície.
 *
 * O que ENTRA aqui: texto, imagem ou payload que saiu de um modelo e chega
 * aos olhos do usuário. O que NÃO entra (e por quê) está no corpo do PR —
 * card de nudge, lembrete, toast de level-up do Brain e sugestão adaptativa
 * do Foco parecem fala do Espelho mas vêm de chave de i18n ou de string
 * fixa em trigger de Postgres. Denunciar texto determinístico não informaria
 * moderação nenhuma; informaria que a copy é ruim.
 *
 * REGRA DO content_id: precisa ser estável ENTRE RECARGAS, senão o corte da
 * parte C não sobrevive ao reload. Onde existe linha, é o uuid dela; onde não
 * existe, é uma chave composta determinística montada pelos helpers abaixo.
 */

export const REPORT_SURFACES = {
  /** Bolha do Espelho no chat principal — id: mirror_messages.id */
  MIRROR_MESSAGE: "mirror_message",
  /** Resposta do MiniMirror por módulo (agenda/brain/fitness/nutrition) — id: mirror_messages.id */
  MIRROR_MINI: "mirror_mini",
  /** Frase de abertura da Home — id: hero:<YYYY-MM-DD> (não persiste em tabela) */
  MIRROR_HERO: "mirror_hero",
  /** Entregável do Executor: texto/docx/pdf/xlsx — id: mirror_deliverables.id */
  DELIVERABLE: "deliverable",
  /** Entregável do Executor: imagem gerada — id: mirror_deliverables.id */
  DELIVERABLE_IMAGE: "deliverable_image",
  /** Resumo Diário — id: daily_briefs.id */
  DAILY_BRIEF: "daily_brief",
  /** Destaque semanal — id: mirror_insights.id */
  WEEKLY_INSIGHT: "weekly_insight",
  /** Resposta do Espelho a uma entrada do diário — id: journal_entries.id */
  JOURNAL_RESPONSE: "journal_response",
  /** Reação do Espelho ao registrar sono — id: sleep_logs.id */
  REACTION_SLEEP: "reaction_sleep",
  /** Reação ao registrar refeição — id: nutrition_logs.id */
  REACTION_NUTRITION: "reaction_nutrition",
  /** Reação ao concluir treino — id: workout_sessions.id */
  REACTION_WORKOUT: "reaction_workout",
  /** Reação a medida corporal — id: body_measurements.id */
  REACTION_BODY: "reaction_body",
  /** Reação a sessão de idioma — id: language_sessions.id */
  REACTION_LANGUAGE: "reaction_language",
  /** Lição de idioma gerada — id: lesson:<nativo>:<alvo>:<nível>:<índice> */
  LANGUAGE_LESSON: "language_lesson",
  /** Exercício do Brain gerado — id: brain:<tipo>:<nível>:<hash do payload> */
  BRAIN_EXERCISE: "brain_exercise",
  /**
   * Análise de foto de refeição — id: photo:<hash da descrição do modelo>.
   *
   * É a única superfície cujo conteúdo NUNCA chegou ao banco: o modal é de
   * confirmação PRÉ-gravação. A exclusão era defensável ("descartar já remove")
   * e foi revertida por decisão do Rafa (19/08): o Google escreveu que o
   * problema aparece em outras áreas, e uma segunda rejeição custa um ciclo
   * inteiro num canal que é 12× o iOS em volume.
   *
   * Consequência de desenho: aqui o corte NÃO é esconder, é DESCARTAR — a
   * denúncia fecha o modal e a análise morre sem virar linha em nutrition_logs.
   * Mais forte que ocultar, e ainda assim registrada para moderação.
   */
  NUTRITION_PHOTO: "nutrition_photo",
} as const;

// NÃO está aqui, e é decisão, não esquecimento:
//
// · Modais de confirmação de captura por voz (agenda / tarefa / hábito /
//   meta / foco / sono / treino / finanças / diário). O modelo estrutura a fala
//   DO PRÓPRIO USUÁRIO e ele aceita ou descarta ali — não é o modelo falando,
//   é o modelo transcrevendo. (A foto é diferente: lá o modelo ESCREVE uma
//   descrição do que acha que está no prato, e por isso entrou.)
// · Texto vindo de chave de i18n ou de string fixa em trigger: card de nudge,
//   lembrete (_shared/reminderTemplates.ts), toast de level-up do Brain
//   (20260518_brain_level_up_trigger.sql) e sugestão adaptativa do Foco.
//   Parecem fala do Espelho e não são — denunciar copy determinística não
//   informaria moderação nenhuma.

export type ReportSurface = (typeof REPORT_SURFACES)[keyof typeof REPORT_SURFACES];

/**
 * Motivos. Curtos de propósito: uma lista longa faz o usuário desistir no meio,
 * e o que a moderação precisa vem do snapshot, não da taxonomia. `other` existe
 * para o campo livre nunca ser obrigatório.
 *
 * ⚠️ Espelha a CHECK constraint content_reports_reason_valid. Motivo novo aqui
 * sem migration = INSERT rejeitado pelo banco.
 */
export const REPORT_REASONS = [
  "offensive",
  "hate",
  "sexual",
  "violence",
  "self_harm",
  "misinformation",
  "harassment",
  "privacy",
  "other",
] as const;

export type ReportReason = (typeof REPORT_REASONS)[number];

export type SnapshotKind = "text" | "image_path" | "json";

/** Teto do snapshot. A CHECK do banco corta em 20 000; truncar aqui evita que
 *  uma resposta longa vire um INSERT rejeitado no momento da denúncia — a hora
 *  em que menos se pode falhar. */
export const SNAPSHOT_MAX_CHARS = 20000;
export const DETAILS_MAX_CHARS = 1000;

export function truncateSnapshot(raw: string): string {
  if (raw.length <= SNAPSHOT_MAX_CHARS) return raw;
  return raw.slice(0, SNAPSHOT_MAX_CHARS - 1) + "…";
}

/**
 * Snapshot nunca pode ser vazio (a coluna é NOT NULL com CHECK >= 1). Conteúdo
 * que rende só imagem, ou texto que chegou vazio por um erro anterior, ainda
 * precisa poder ser denunciado — o relato é sobre o que a pessoa viu.
 */
export function safeSnapshot(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  return s.length > 0 ? truncateSnapshot(s) : "[sem texto]";
}

/** Chave composta determinística — mesma entrada, mesma chave, entre recargas. */
function composite(prefix: string, parts: Array<string | number>): string {
  return prefix + ":" + parts.map((p) => String(p)).join(":");
}

/** Hash estável e curto para payloads sem id (exercício do Brain, foto). djb2. */
export function stableHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Hero da Home: um por usuário por dia local. */
export function heroContentId(dayYmd: string): string {
  return composite("hero", [dayYmd]);
}

/** Lição de idioma: a MESMA chave que o cache do edge fn usa. */
export function languageLessonContentId(params: {
  nativeLang: string;
  learningLang: string;
  level: string;
  lessonIndex: number;
}): string {
  return composite("lesson", [
    params.nativeLang,
    params.learningLang,
    params.level,
    params.lessonIndex,
  ]);
}

/** Exercício do Brain: não há linha, então o próprio payload é a identidade. */
export function brainExerciseContentId(params: {
  challengeType: string;
  level: number;
  payload: unknown;
}): string {
  let serialized: string;
  try {
    serialized = JSON.stringify(params.payload) ?? "";
  } catch (err) {
    console.warn("[report/surfaces] brain payload não serializou", err);
    serialized = String(params.payload);
  }
  return composite("brain", [params.challengeType, params.level, stableHash(serialized)]);
}

/** Análise de foto: idem — o texto devolvido é a identidade quando não há linha. */
export function nutritionPhotoContentId(description: string): string {
  return composite("photo", [stableHash(description)]);
}
